const FALLBACK = {
  ink: '#1e293b', inkSoft: '#475569', inkMute: '#94a3b8',
  hairline: '#e2e8f0', hairlineStrong: '#cbd5e1',
  primary: '#2563eb', primaryDeep: '#1d4ed8', primaryLight: '#60a5fa',
  secondary: '#1e293b', secondarySoft: '#475569',
  accent: '#3b82f6', bgLight: '#F3F4F6', surface: '#ffffff',
  danger: '#ef4444',
};
const VARMAP = {
  ink: '--brand-secondary',
  inkSoft: '--brand-secondary',
  inkMute: '--brand-secondary',
  hairline: '--hairline',
  hairlineStrong: '--hairline-strong',
  primary: '--brand-primary',
  secondary: '--brand-secondary',
  accent: '--brand-accent',
  bgLight: '--brand-bg-light',
  surface: '--surface',
  danger: '--danger',
};

let C = { ...FALLBACK };

export function refreshTokens() {
  if (typeof document === 'undefined') return C;
  const cs = getComputedStyle(document.documentElement);
  const next = {};
  for (const [k, v] of Object.entries(VARMAP)) {
    const val = cs.getPropertyValue(v).trim();
    next[k] = val || FALLBACK[k];
  }
  
  // Ajustes de fallback dinâmico
  next.ink = next.secondary;
  next.inkSoft = '#475569';
  next.inkMute = '#94a3b8';
  next.hairline = '#e2e8f0';
  next.hairlineStrong = '#cbd5e1';
  next.surface = '#ffffff';
  next.danger = '#ef4444';
  
  C = next;
  return C;
}
if (typeof document !== 'undefined') refreshTokens();

export const TOKENS = new Proxy({}, { get: (_, k) => C[k] });

export const CHART_PALETTE = ['#FF9D00', '#1D1D1B', '#FFE100', '#10b981', '#ef4444'];

const FONT = '"Jost", "Corbel", "Gill Sans", system-ui, sans-serif';

const nf = new Intl.NumberFormat('pt-BR');
export function fmtNum(v) { return nf.format(v); }
export function fmtCompact(v) {
  if (v >= 1000000) return `${(v / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  return nf.format(v);
}

export function baseChart(extra = {}) {
  return {
    color: [TOKENS.primary, TOKENS.secondary, TOKENS.accent, '#10b981', '#ef4444'],
    textStyle: { fontFamily: FONT, color: TOKENS.ink, fontSize: 12 },
    grid: { left: 8, right: 16, top: 28, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: TOKENS.surface,
      borderColor: TOKENS.hairline,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: { color: TOKENS.ink, fontFamily: FONT, fontSize: 12.5, fontWeight: 500 },
      valueFormatter: (v) => fmtNum(v),
      extraCssText: 'box-shadow: 0 12px 30px -18px rgba(29, 29, 27, 0.28); border-radius: 10px;',
      axisPointer: { lineStyle: { color: TOKENS.hairlineStrong, type: 'dashed' } },
    },
    ...extra,
  };
}

export function lineSeries(name, data, color = TOKENS.primary) {
  const rgba = (a) => {
    const m = color.replace('#', '');
    const r = parseInt(m.slice(0, 2), 16) || 37;
    const g = parseInt(m.slice(2, 4), 16) || 99;
    const b = parseInt(m.slice(4, 6), 16) || 235;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };
  return {
    name,
    type: 'line',
    data,
    smooth: 0.3,
    symbol: 'circle',
    symbolSize: 6,
    showSymbol: false,
    itemStyle: { color, borderColor: TOKENS.surface, borderWidth: 2 },
    lineStyle: { width: 1.75, color },
    emphasis: { focus: 'series', scale: 1.2 },
    areaStyle: {
      color: {
        type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: rgba(0.22) },
          { offset: 1, color: rgba(0.02) },
        ],
      },
    },
  };
}

export function axisLine(label = '') {
  return {
    type: 'category',
    name: label,
    nameLocation: 'middle',
    nameGap: 28,
    boundaryGap: false,
    nameTextStyle: { color: TOKENS.inkMute, fontFamily: FONT, fontSize: 11 },
    axisLine: { lineStyle: { color: TOKENS.hairlineStrong } },
    axisTick: { show: false },
    axisLabel: { color: TOKENS.inkSoft, fontFamily: FONT, fontSize: 11 },
  };
}

export function axisValue(extra = {}) {
  return {
    type: 'value',
    splitNumber: 4,
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: TOKENS.hairline } },
    axisLabel: { color: TOKENS.inkMute, fontFamily: FONT, fontSize: 11, formatter: (v) => fmtCompact(v) },
    ...extra,
  };
}

export { FONT };
